import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, ArrowUp, ArrowDown, Calendar } from 'lucide-react';
import { useUser } from '../../providers/user';
import { googleSheetsService } from '../../services/google-sheets';
import { getFinanceTrackerTitle } from '../../constants/common';
import { formatCurrency } from '../../utils/currency';

const MonthlyOverview = ({ selectedYear: propSelectedYear, showAmounts = true, expenses: propExpenses = [], categories = [], currency = 'USD', showCurrency = true }) => {
  const [selectedMonthDetail, setSelectedMonthDetail] = useState(null);
  const [selectedYear, setSelectedYear] = useState(propSelectedYear);
  const [availableYears, setAvailableYears] = useState([]);
  const [expensesCache, setExpensesCache] = useState({ [propSelectedYear]: propExpenses });
  const [isLoadingYears, setIsLoadingYears] = useState(false);
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
  const { currentUser } = useUser();

  // Use cached expenses for the selected year, or empty array
  const expenses = expensesCache[selectedYear] || [];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getMonthlyExpenses = (monthIndex) => {
    return expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate.getMonth() === monthIndex;
    });
  };

  const getMonthlyTotal = (monthIndex) => {
    const monthExpenses = getMonthlyExpenses(monthIndex);
    return monthExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  };

  const getCategoryTotal = (monthIndex) => {
    const monthExpenses = getMonthlyExpenses(monthIndex);
    const categoryTotals = {};
    monthExpenses.forEach(expense => {
      if (!categoryTotals[expense.category]) {
        categoryTotals[expense.category] = 0;
      }
      categoryTotals[expense.category] += expense.amount;
    });
    return categoryTotals;
  };

  const getSpendingTrend = (monthIndex) => {
    if (monthIndex === 0) return null;

    const currentMonthTotal = getMonthlyTotal(monthIndex);
    const previousMonthTotal = getMonthlyTotal(monthIndex - 1);

    if (previousMonthTotal === 0) return null;

    const percentageChange = ((currentMonthTotal - previousMonthTotal) / previousMonthTotal) * 100;

    return {
      trend: percentageChange > 0 ? 'up' : percentageChange < 0 ? 'down' : 'same',
      percentage: Math.abs(percentageChange)
    };
  };

  const getYearlyTotal = () => {
    return expenses.reduce((sum, expense) => sum + expense.amount, 0);
  };

  const formatAmount = (amount) => {
    return formatCurrency(amount, currency, showCurrency, showAmounts);
  };

  const getBudgetStatus = (category, amount) => {
    // Find the budget for this category from categories array
    const categoryData = categories.find(c => c.category === category);
    if (!categoryData || !categoryData.budget) return null;

    const budget = categoryData.budget;
    const remaining = budget - amount;
    return {
      budget,
      remaining,
      isOverBudget: remaining < 0,
      overage: remaining < 0 ? Math.abs(remaining) : 0
    };
  };

  // Get all unique subcategories from the categories
  const getAllSubCategories = () => {
    const subCats = [...new Set(categories.map(c => c.subCategory))];
    return subCats.filter(sc => sc); // Filter out empty strings
  };

  // Load available years on mount
  useEffect(() => {
    const loadYears = async () => {
      if (!currentUser?.accessToken) return;

      setIsLoadingYears(true);
      try {
        googleSheetsService.setAccessToken(currentUser.accessToken);
        const years = await googleSheetsService.searchFinanceTrackerYears();

        // Add current year if not already in the list
        const currentYear = new Date().getFullYear();
        const yearsSet = new Set(years);
        yearsSet.add(currentYear);
        yearsSet.add(propSelectedYear); // Also add the prop year

        const sortedYears = Array.from(yearsSet).sort((a, b) => b - a);
        setAvailableYears(sortedYears);
      } catch (error) {
        console.error('Error loading years:', error);
        // Fallback to current year
        setAvailableYears([new Date().getFullYear(), propSelectedYear]);
      } finally {
        setIsLoadingYears(false);
      }
    };

    loadYears();
  }, [currentUser?.accessToken, propSelectedYear]);

  // Update cache when propExpenses changes
  useEffect(() => {
    setExpensesCache(prev => ({
      ...prev,
      [propSelectedYear]: propExpenses
    }));
  }, [propExpenses, propSelectedYear]);

  // Fetch expenses when year changes (if not already cached)
  const handleYearChange = async (year) => {
    const newYear = parseInt(year, 10);
    setSelectedYear(newYear);

    // If we already have data for this year, just switch to it
    if (expensesCache[newYear]) {
      return;
    }

    // Otherwise, fetch the data
    setIsLoadingExpenses(true);
    try {
      googleSheetsService.setAccessToken(currentUser.accessToken);

      // Get the expense sheet ID from localStorage or search
      const sheetTitle = getFinanceTrackerTitle(newYear);
      let expenseSheetId = localStorage.getItem(`finance_sheet_${newYear}`);

      if (expenseSheetId) {
        const valid = await googleSheetsService.verifySpreadsheet(expenseSheetId);
        if (!valid) {
          localStorage.removeItem(`finance_sheet_${newYear}`);
          expenseSheetId = null;
        }
      }

      if (!expenseSheetId) {
        const found = await googleSheetsService.searchSpreadsheetByName(sheetTitle);
        if (found && found.spreadsheetId) {
          expenseSheetId = found.spreadsheetId;
          localStorage.setItem(`finance_sheet_${newYear}`, expenseSheetId);
        }
      }

      if (expenseSheetId) {
        // Fetch expenses from the sheet
        const response = await googleSheetsService.getSheetData(
          expenseSheetId,
          'A2:G1000'
        );

        if (response.values && response.values.length > 0) {
          const parsedExpenses = response.values
            .filter(row => row[0] && row[1] && row[2]) // Must have date, amount, and category
            .map((row, index) => ({
              id: `${row[0]}-${index}`,
              date: row[0],
              amount: parseFloat(row[1]) || 0,
              category: row[2],
              subCategory: row[3] || '',
              additionalInfo: row[4] || '',
              month: row[5] || '',
              year: parseInt(row[6]) || newYear
            }))
            .filter(expense => expense.year === newYear);

          // Cache the fetched expenses
          setExpensesCache(prev => ({
            ...prev,
            [newYear]: parsedExpenses
          }));
        } else {
          // No data found, cache empty array
          setExpensesCache(prev => ({
            ...prev,
            [newYear]: []
          }));
        }
      } else {
        // No sheet found for this year, cache empty array
        setExpensesCache(prev => ({
          ...prev,
          [newYear]: []
        }));
      }
    } catch (error) {
      console.error('Error fetching expenses for year:', error);
      // On error, cache empty array
      setExpensesCache(prev => ({
        ...prev,
        [newYear]: []
      }));
    } finally {
      setIsLoadingExpenses(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mt-10 mb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Monthly Overview</h2>
        <div className="flex items-center gap-4">
          {/* Year Dropdown */}
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-gray-500" />
            <select
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
              disabled={isLoadingYears || isLoadingExpenses}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
            >
              {availableYears.length === 0 && (
                <option value={selectedYear}>{selectedYear}</option>
              )}
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {isLoadingExpenses && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            )}
          </div>

          {/* Total Badge */}
          <div className="flex items-center gap-2 px-4 py-2 bg-green-100 rounded-lg">
            <TrendingUp size={20} className="text-green-600" />
            <span className="font-semibold text-green-800">
              Total: {formatAmount(getYearlyTotal())}
            </span>
          </div>
        </div>
      </div>

      {expenses.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No expenses recorded for {selectedYear}</p>
          <p className="text-gray-400 text-sm mt-2">Add your first expense to see the monthly overview</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-1 gap-4">
          {months.map((month, index) => {
            const monthlyTotal = getMonthlyTotal(index);
            const monthlyExpenses = getMonthlyExpenses(index);
            const categoryTotals = getCategoryTotal(index);
            const spendingTrend = getSpendingTrend(index);

            // Only show months with data
            if (monthlyExpenses.length === 0) return null;

            return (
              <div key={month} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-800">{month}</h3>
                    {spendingTrend && (
                      <div className="flex items-center">
                        {spendingTrend.trend === 'up' ? (
                          <ArrowUp size={14} className="text-red-500" />
                        ) : spendingTrend.trend === 'down' ? (
                          <ArrowDown size={14} className="text-green-500" />
                        ) : null}
                        <span className={`text-xs ${spendingTrend.trend === 'up' ? 'text-red-500' : 'text-green-500'}`}>
                          {spendingTrend.percentage.toFixed(0)}%
                        </span>
                      </div>
                    )}
                    {monthlyExpenses.length > 0 && (
                      <button
                        onClick={() => setSelectedMonthDetail(selectedMonthDetail === index ? null : index)}
                        className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors"
                      >
                        Details
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-bold text-blue-600">
                      {formatAmount(monthlyTotal)}
                    </span>
                  </div>
                </div>

                {monthlyExpenses.length === 0 ? (
                  <p className="text-gray-500 text-sm">No expenses</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      {monthlyExpenses.length} expense{monthlyExpenses.length !== 1 ? 's' : ''}
                    </p>
                    {Object.entries(categoryTotals).map(([category, total]) => {
                      const budgetStatus = getBudgetStatus(category, total);

                      return (
                        <div key={category} className="space-y-1">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-700 truncate mr-2">
                              {category.replace(/[🏠🥗🚙🛁📱🛍️🖼️✈️📚]/g, '').trim()}
                            </span>
                            <div className="relative group">
                              <span className={`font-medium ${budgetStatus?.isOverBudget ? 'text-red-600 cursor-pointer' : 'text-gray-900'}`}>
                                {formatAmount(total)}
                              </span>
                              {budgetStatus?.isOverBudget && (
                                <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                  Overbudget by {formatAmount(budgetStatus.overage)}
                                  <div className="absolute top-full right-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                                </div>
                              )}
                            </div>
                          </div>
                          {budgetStatus && (
                            <div className="text-xs">
                              {budgetStatus.isOverBudget ? (
                                <span className="text-red-500">
                                  Over budget by {formatAmount(budgetStatus.overage)}
                                </span>
                              ) : (
                                <span className="text-green-600">
                                  {formatAmount(budgetStatus.remaining)} remaining
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {selectedMonthDetail === index && (
                  <div className="mt-6 pt-4 border-t border-gray-300">
                    <h4 className="text-base font-semibold text-gray-800 mb-4">Sub-Category Breakdown</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full bg-white rounded-lg">
                        <thead>
                          <tr className="bg-gray-50">
                            {(() => {
                              const allSubCategories = getAllSubCategories();
                              return allSubCategories.map((subCat, idx) => (
                                <th key={idx} className="text-left py-3 px-3 text-sm font-medium text-gray-600 min-w-28">
                                  {subCat.length > 12 ? subCat.substring(0, 12) + '...' : subCat}
                                </th>
                              ));
                            })()}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const allSubCategories = getAllSubCategories();
                            const monthExpenses = getMonthlyExpenses(index);
                            const subCategoryExpenses = {};

                            allSubCategories.forEach(subCat => {
                              subCategoryExpenses[subCat] = [];
                            });

                            monthExpenses.forEach(expense => {
                              if (subCategoryExpenses[expense.subCategory] !== undefined) {
                                subCategoryExpenses[expense.subCategory].push(expense);
                              }
                            });

                            const maxExpenses = Math.max(...allSubCategories.map(subCat => subCategoryExpenses[subCat].length), 0);
                            const rows = [];

                            for (let rowIndex = 0; rowIndex < maxExpenses; rowIndex++) {
                              rows.push(
                                <tr key={rowIndex}>
                                  {allSubCategories.map(subCat => {
                                    const expenses = subCategoryExpenses[subCat];
                                    const expense = expenses[rowIndex];
                                    const amount = expense?.amount;
                                    const additionalInfo = expense?.additionalInfo || '';
                                    return (
                                      <td key={subCat} className="py-2 px-3 text-left">
                                        <div className="relative group">
                                          <span className={amount ? `text-blue-600 font-normal text-base ${additionalInfo ? 'cursor-pointer' : ''}` : 'text-gray-300'}>
                                            {amount ? formatAmount(amount) : '—'}
                                          </span>
                                          {additionalInfo && (
                                            <div className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                              {additionalInfo}
                                              <div className="absolute top-full left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            }

                            if (maxExpenses > 0) {
                              rows.push(
                                <tr key="total" className="border-t border-gray-200 bg-gray-50">
                                  {allSubCategories.map(subCat => {
                                    const expenses = subCategoryExpenses[subCat];
                                    const total = expenses.reduce((sum, expense) => sum + (expense?.amount || 0), 0);
                                    return (
                                      <td key={subCat} className="py-3 px-3 text-left">
                                        <span className={total > 0 ? 'text-gray-800 font-semibold text-base' : 'text-gray-400'}>
                                          {total > 0 ? formatAmount(total) : '—'}
                                        </span>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            }

                            return rows.length > 0 ? rows : (
                              <tr>
                                <td colSpan={allSubCategories.length} className="py-4 px-3 text-center text-gray-500">
                                  No expenses found for this month
                                </td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MonthlyOverview;
